import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DlBtn from '../DlBtn';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { moneyTight, moneyWithCurrency, localeDate, tz } from '../../utils/region';

// Filing Center — cross-client RD Prep export for the Revenue Department withholding returns (ภ.ง.ด.1/3/53).
// Pick year + month + form → see every company that has data → download per company or a zip of all,
// and track when each was last exported / mark it filed. (ภ.พ.30 / VAT summary is added later.)

type Form = 'pnd1' | 'pnd3' | 'pnd53';
type Row = { clientId: string; name: string; nameTh: string; taxId: string; count: number; wht: number; exportedAt: number; filedAt: number };

const FORMS: [Form, string][] = [['pnd1', 'ภ.ง.ด.1'], ['pnd3', 'ภ.ง.ด.3'], ['pnd53', 'ภ.ง.ด.53']];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDT = (u: number) => u ? new Date(u * 1000).toLocaleString(localeDate(), { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz() }) : '';

// 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were light grey in heavy bold (800), 10px above and below where it was 9, on one 16px line, over the darker
// --border-strong line — a 37px band, as every list's is.
// 10 → 5px under the heading text (report c151d269: "Bring the heading text 5 pixels closer to the grey line
// below. Do this for every page." — see Active Jobs' th; the uppercase and the letter-spacing stay).
const th: React.CSSProperties = { padding: '10px 10px 5px', textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', letterSpacing: '.03em', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };

// ── Tax Payment Request (2026-08-03, owner-approved) ────────────────────────
// "These clients' taxes are ready — please pay": pick a tax kind, tick clients, PREVIEW the email
// (recipient editable there — nothing sends without a human looking at it), then Send. Default
// recipient is the authorized payer (K. Nacha); sender is office@slf-accounting.co. Amounts come
// from the same backend functions as the Filing Center cards, so screen and email always agree.
type PayRow = { clientId: string; name: string; tax?: number; penalty?: number; amount: number; requested: boolean };
const PAY_KINDS: [string, string][] = [['vat', 'VAT (PP.30)'], ['pnd1', 'PND.1'], ['pnd3', 'PND.3'], ['pnd53', 'PND.53'], ['sso', 'SSO'], ['pnd51', 'PND.51'], ['pnd50', 'PND.50'], ['pnd94', 'PND.94'], ['pnd9091', 'PND.90/91']];
// PND.51 is an ANNUAL return (owner 2026-08-29): the month picker above the page does not apply
// to it, so the card sends month 0 and the server keys the whole request on the year alone.
const PAY_ANNUAL = new Set(['pnd51', 'pnd50', 'pnd94', 'pnd9091']);

function PaymentRequestCard({ year, month }: { year: number; month: number }) {
  const [kind, setKind] = useState('pnd1');
  const [rows, setRows] = useState<PayRow[]>([]);
  const [due, setDue] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  // Preview dialog state — `to`/`cc` are editable, the body is server-rendered and read-only.
  const [pv, setPv] = useState<any>(null);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [busy, setBusy] = useState<'' | 'preview' | 'send'>('');
  const [done, setDone] = useState('');
  const [err, setErr] = useState('');

  const annual = PAY_ANNUAL.has(kind);
  const payMonth = annual ? 0 : month;

  const load = useCallback(async () => {
    setLoading(true); setErr(''); setDone('');
    try { const r: any = await api.payReqList(kind, year, payMonth); setRows(r.rows || []); setDue(r.dueDate || ''); setSel(new Set()); }
    catch (e: any) { setRows([]); setErr(e.message); }
    finally { setLoading(false); }
  }, [kind, year, payMonth]);
  useEffect(() => { if (open) load(); }, [load, open]);

  const toggle = (id: string) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = rows.length > 0 && sel.size === rows.length;
  const total = rows.filter(r => sel.has(r.clientId)).reduce((a, r) => a + r.amount, 0);

  const preview = async () => {
    setBusy('preview'); setErr('');
    try {
      const r: any = await api.payReqPreview({ kind, year, month: payMonth, clientIds: Array.from(sel) });
      setPv(r); setTo((r.to || []).join(', ')); setCc((r.cc || []).join(', '));
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const send = async () => {
    setBusy('send'); setErr('');
    try {
      const r: any = await api.payReqSend({ kind, year, month: payMonth, clientIds: Array.from(sel), to, cc });
      setPv(null); setDone(`Sent to ${r.sent} — ${r.clientCount} client(s), ${moneyWithCurrency(r.total)}.`);
      load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', marginBottom: 16, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', cursor: 'pointer', background: 'var(--c-gray-50)' }}>
        <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ color: 'var(--text-tertiary)' }} />
        <i className="ti ti-cash-banknote" style={{ color: 'var(--accent)' }} />
        <div style={{ fontSize: 13.5, fontWeight: 800 }}>Tax Payment Request</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>notify the authorized payer which clients' taxes to pay</div>
      </div>
      {open && (
        <div style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
              {PAY_KINDS.map(([k, lbl], i) => (
                <button key={k} onClick={() => setKind(k)} style={{ padding: '6px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: 'none', borderRight: i < PAY_KINDS.length - 1 ? '1px solid var(--border)' : 'none', background: kind === k ? 'var(--bg-active)' : 'var(--bg-surface)', color: kind === k ? 'var(--accent)' : 'var(--text-secondary)' }}>{lbl}</button>
              ))}
            </span>
            {annual && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Tax year: <b style={{ color: 'var(--text-primary)' }}>{year}</b></span>}
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Deadline: <b style={{ color: 'var(--text-primary)' }}>{due || '—'}</b></span>
            <div style={{ flex: 1 }} />
            <button onClick={preview} disabled={!sel.size || !!busy}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (!sel.size || busy) ? 'not-allowed' : 'pointer', opacity: (!sel.size || busy) ? 0.55 : 1 }}>
              <i className={`ti ${busy === 'preview' ? 'ti-loader-2' : 'ti-mail-forward'}`} style={{ animation: busy === 'preview' ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} />
              Request Payment{sel.size ? ` (${sel.size})` : ''}
            </button>
          </div>
          {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 8, padding: '7px 11px', marginBottom: 8 }}>{err}</div>}
          {done && <div style={{ fontSize: 12.5, color: '#15803D', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 8, padding: '7px 11px', marginBottom: 8 }}><i className="ti ti-circle-check" style={{ marginRight: 5 }} />{done}</div>}
          {loading ? <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: 8 }}>Loading…</div>
            : !rows.length ? <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: 8 }}>No client has a payable {PAY_KINDS.find(k => k[0] === kind)?.[1]} amount for this {annual ? 'year' : 'month'}.</div>
            : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...th, width: 36, textAlign: 'center' }}><input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? new Set() : new Set(rows.map(r => r.clientId)))} style={{ cursor: 'pointer' }} /></th>
                <th style={th}>Client</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                <th style={{ ...th, width: 130 }}>Status</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.clientId} style={{ background: sel.has(r.clientId) ? '#EFF6FF' : undefined }}>
                    <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={sel.has(r.clientId)} onChange={() => toggle(r.clientId)} style={{ cursor: 'pointer' }} /></td>
                    <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                      {moneyTight(r.amount)}
                      {(r.penalty || 0) > 0 && (
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: '#B45309' }}>
                          Tax {moneyTight(r.tax || 0)} + Penalty {moneyTight(r.penalty || 0)}
                        </div>
                      )}
                    </td>
                    <td style={td}>{r.requested
                      ? <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#ECFDF5', color: '#15803D' }}>REQUESTED</span>
                      : <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>—</span>}</td>
                  </tr>
                ))}
                <tr><td style={td} /><td style={{ ...td, fontWeight: 800 }}>Selected: {sel.size}</td><td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{moneyTight(total)}</td><td style={td} /></tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {pv && (
        <div onClick={e => { if (e.target === e.currentTarget && busy !== 'send') setPv(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 12, width: 'min(720px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 800 }}>Review before sending</div>
            <div style={{ padding: '12px 18px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: '8px 10px', alignItems: 'center', marginBottom: 10, fontSize: 13 }}>
                <b>From</b><span>{pv.from}</span>
                <b>To</b><input value={to} onChange={e => setTo(e.target.value)} style={{ padding: '7px 10px', border: '1px solid var(--border-strong)', borderRadius: 7, fontSize: 13, background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
                <b>CC</b><input value={cc} onChange={e => setCc(e.target.value)} style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
                <b>Subject</b><span style={{ fontWeight: 600 }}>{pv.subject}</span>
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', background: '#fff', color: '#111' }}
                dangerouslySetInnerHTML={{ __html: pv.bodyHtml }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
              <button onClick={() => setPv(null)} disabled={busy === 'send'} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={send} disabled={busy === 'send' || !to.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy === 'send' ? 'default' : 'pointer', opacity: busy === 'send' ? 0.7 : 1 }}>
                <i className={`ti ${busy === 'send' ? 'ti-loader-2' : 'ti-send'}`} style={{ animation: busy === 'send' ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} />
                {busy === 'send' ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ภ.พ.30 (VAT) monthly summary ────────────────────────────────────────────
// PP.30 is a SUMMARY return — one set of figures per company, keyed into e-Filing — so unlike
// ภ.ง.ด.1/3/53 there is no per-company import file, just the cross-client table the operator works
// down, plus its Excel. Every figure comes from the same source as the client's own VAT page and
// the credit chain; nothing here is recomputed from a percentage.
type Pp30Row = {
  clientId: string; name: string; nameTh: string; taxId: string;
  salesBase: number; salesZero: number; outputVat: number;
  purchaseBase: number; inputVat: number;
  net: number; brought: number; payable: number; carry: number; filed: boolean; lines: number;
};

function Pp30Card({ year, month }: { year: number; month: number }) {
  const [rows, setRows] = useState<Pp30Row[]>([]);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { const r: any = await api.pp30Summary(year, month); setRows(r.rows || []); }
    catch (e: any) { setRows([]); setErr(e.message); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { if (open) load(); }, [load, open]);

  const sum = (f: (r: Pp30Row) => number) => rows.reduce((a, r) => a + f(r), 0);
  const download = async () => {
    setBusy(true); setErr('');
    try { await api.pp30Export(year, month); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const nz = (n: number) => n ? moneyTight(n) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', marginBottom: 16, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', cursor: 'pointer', background: 'var(--c-gray-50)' }}>
        <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ color: 'var(--text-tertiary)' }} />
        <i className="ti ti-file-spreadsheet" style={{ color: 'var(--accent)' }} />
        <div style={{ fontSize: 13.5, fontWeight: 800 }}>ภ.พ.30 · VAT summary</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>every VAT client's figures for the month, ready to key into e-Filing</div>
      </div>
      {open && (
        <div style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              <b style={{ color: 'var(--text-primary)' }}>{rows.length}</b> companies · payable <b style={{ color: 'var(--text-primary)' }}>{moneyTight(sum(r => r.payable))}</b>
            </span>
            <div style={{ flex: 1 }} />
            <button onClick={download} disabled={busy || !rows.length}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (busy || !rows.length) ? 'not-allowed' : 'pointer', opacity: (busy || !rows.length) ? 0.55 : 1 }}>
              <i className={`ti ${busy ? 'ti-loader-2' : 'ti-download'}`} style={{ animation: busy ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} /> Download Excel
            </button>
          </div>
          {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 8, padding: '7px 11px', marginBottom: 8 }}>{err}</div>}
          {loading ? <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: 8 }}>Loading…</div>
            : !rows.length ? <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: 8 }}>No VAT-registered client has activity or a carried credit for this month.</div>
            : (
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 940 }}>
              <thead><tr>
                <th style={th}>Company</th>
                <th style={{ ...th, textAlign: 'right' }}>Sales</th>
                <th style={{ ...th, textAlign: 'right' }}>Output VAT</th>
                <th style={{ ...th, textAlign: 'right' }}>Purchases</th>
                <th style={{ ...th, textAlign: 'right' }}>Input VAT</th>
                <th style={{ ...th, textAlign: 'right' }}>Net</th>
                <th style={{ ...th, textAlign: 'right' }}>Credit b/f</th>
                <th style={{ ...th, textAlign: 'right' }}>Payable</th>
                <th style={{ ...th, textAlign: 'right' }}>Credit c/f</th>
                <th style={{ ...th, textAlign: 'center', width: 70 }}>Filed</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.clientId}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {r.name}
                      {/* A nil return still has to be filed — say so, or it looks like a stray row. */}
                      {r.lines === 0 && <span title="No invoices recorded this month — a nil PP.30 is filed to carry the credit forward." style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: '#FFFBEB', color: '#B45309' }}>NIL</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{nz(r.salesBase)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{nz(r.outputVat)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{nz(r.purchaseBase)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{nz(r.inputVat)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{nz(r.net)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#1D9E75' }}>{nz(r.brought)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{nz(r.payable)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#1D9E75' }}>{nz(r.carry)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{r.filed
                      ? <i className="ti ti-circle-check" style={{ color: '#15803D' }} />
                      : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--border-strong)' }}>
                  <td style={{ ...td, fontWeight: 800 }}>TOTAL</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{moneyTight(sum(r => r.salesBase))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{moneyTight(sum(r => r.outputVat))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{moneyTight(sum(r => r.purchaseBase))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{moneyTight(sum(r => r.inputVat))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{moneyTight(sum(r => r.net))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{moneyTight(sum(r => r.brought))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{moneyTight(sum(r => r.payable))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{moneyTight(sum(r => r.carry))}</td>
                  <td style={td} />
                </tr>
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FilingCenter() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const now = new Date();
  let pY = now.getFullYear(), pM = now.getMonth() + 1 - 1; if (pM === 0) { pM = 12; pY -= 1; }   // default = previous month
  const nowY = now.getFullYear();
  const years: number[] = []; for (let y = 2024; y <= nowY; y++) years.push(y);
  // ?form=&year=&month= opens one form and month — the "Open →" of each group in the twice-monthly report of
  // returns not yet ticked Filed (report d5e686b4, server/src/services/filingUnfiledReport.ts).
  const qYear = Number(params.get('year')), qMonth = Number(params.get('month')), qForm = params.get('form') || '';

  const [year, setYear] = useState(() => (years.includes(qYear) ? qYear : pY));
  const [month, setMonth] = useState(() => (qMonth >= 1 && qMonth <= 12 ? qMonth : pM));
  const [form, setForm] = useState<Form>(() => (FORMS.some(([k]) => k === qForm) ? (qForm as Form) : 'pnd3'));
  const [rows, setRows] = useState<Row[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r: any = await api.filingCompanies(form, year, month); setRows((r?.rows || []) as Row[]); setSel(new Set()); }
    catch { setRows([]); }
    finally { setLoading(false); }
  }, [form, year, month]);
  useEffect(() => { load(); }, [load]);

  const allSel = rows.length > 0 && sel.size === rows.length;
  const toggleAll = () => setSel(allSel ? new Set() : new Set(rows.map(r => r.clientId)));
  const toggle = (id: string) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const totalWht = useMemo(() => rows.reduce((a, r) => a + (r.wht || 0), 0), [rows]);
  const selRows = rows.filter(r => sel.has(r.clientId));
  const selWht = selRows.reduce((a, r) => a + (r.wht || 0), 0);

  // Every RD Prep export asks, in two steps, whether the returns went in (report d5e686b4: exported again and again,
  // ticked Filed never). Step 1: "Have you submitted these N returns on the RD website?" — Not yet closes it, and the
  // rows stay marked "Exported, not filed" (exported after the last Filed tick). Step 2 (Yes) lists the companies
  // with a "Mark N as filed" confirm, which ticks Filed through the same call as the tick box. Each company there
  // carries its own tick (all ticked to start), so a return that has not gone in yet can be left out of N.
  const [ask, setAsk] = useState<{ step: 1 | 2; form: Form; year: number; month: number; rows: Row[]; ids: string[] } | null>(null);
  const [askBusy, setAskBusy] = useState(false);
  const [askErr, setAskErr] = useState('');
  const askFiled = (ids: string[]) => {
    const picked = rows.filter(r => ids.includes(r.clientId));
    if (picked.length) { setAskErr(''); setAsk({ step: 1, form, year, month, rows: picked, ids: picked.map(r => r.clientId) }); }
  };
  const confirmFiled = async () => {
    if (!ask || !ask.ids.length) return;
    setAskBusy(true); setAskErr('');
    try {
      await api.filingSetFiled({ form: ask.form, year: ask.year, month: ask.month, clientIds: ask.ids, filed: true });
      setAsk(null); load();
    } catch (e: any) { setAskErr(e.message); } finally { setAskBusy(false); }
  };

  const dlOne = (r: Row) => api.filingDownloadFile(form, r.clientId, year, month).then(() => { askFiled([r.clientId]); return load(); });
  const dlZip = async (ids: string[]) => {
    if (!ids.length) return;
    setBusy(true);
    try { await api.filingDownloadZip(form, year, month, ids); load(); askFiled(ids); } catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };
  const setFiled = async (ids: string[], filed: boolean) => {
    if (!ids.length) return;
    const at = filed ? Math.floor(Date.now() / 1000) : 0;
    setRows(rs => rs.map(r => ids.includes(r.clientId) ? { ...r, filedAt: at } : r));
    try { await api.filingSetFiled({ form, year, month, clientIds: ids, filed }); } catch (e: any) { alert(e.message); load(); }
  };

  const selCss: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('fc_title')}</div>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 700 }}>{t('fc_year')}</label>
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={selCss}>{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
        <label style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 700 }}>{t('fc_month')}</label>
        <select value={month} onChange={e => setMonth(Number(e.target.value))} style={selCss}>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
        <button onClick={() => dlZip(rows.map(r => r.clientId))} disabled={busy || !rows.length}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (busy || !rows.length) ? 'not-allowed' : 'pointer', opacity: (busy || !rows.length) ? 0.55 : 1 }}>
          <i className="ti ti-package" /> {t('fc_download_all')}
        </button>
      </div>

      <PaymentRequestCard year={year} month={month} />

      <Pp30Card year={year} month={month} />

      {/* Form selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em', fontWeight: 800 }}>{t('fc_rd')}</span>
        <span style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
          {FORMS.map(([k, lbl], i) => (
            <button key={k} onClick={() => setForm(k)} style={{ padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none', borderRight: i < FORMS.length - 1 ? '1px solid var(--border)' : 'none', background: form === k ? 'var(--bg-active)' : 'var(--bg-surface)', color: form === k ? 'var(--accent)' : 'var(--text-secondary)' }}>{lbl}</button>
          ))}
        </span>
      </div>

      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>
        <i className="ti ti-arrow-right" style={{ marginRight: 4 }} />{t('fc_export_note')} · <b style={{ color: 'var(--text-primary)' }}>{rows.length}</b> {t('fc_companies')} · {t('fc_wht')} <b style={{ color: 'var(--text-primary)' }}>{moneyTight(totalWht)}</b>
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 40, textAlign: 'center' }}><input type="checkbox" checked={allSel} onChange={toggleAll} style={{ cursor: 'pointer' }} /></th>
              <th style={{ ...th, width: 40, textAlign: 'right' }}>#</th>
              <th style={th}>{t('fc_company')}</th>
              <th style={th}>{t('fc_taxid')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('fc_count')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('fc_wht')}</th>
              <th style={th}>{t('fc_last_export')}</th>
              <th style={{ ...th, textAlign: 'center', width: 120 }}>{t('fc_filed')}</th>
              <th style={{ ...th, textAlign: 'center', width: 56 }}>{t('fc_download')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 28 }}>{t('fc_loading')}</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 28 }}>{t('fc_no_data')}</td></tr>}
            {!loading && rows.map((r, i) => (
              <tr key={r.clientId} style={{ background: sel.has(r.clientId) ? 'var(--bg-active)' : undefined }}>
                <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={sel.has(r.clientId)} onChange={() => toggle(r.clientId)} style={{ cursor: 'pointer' }} /></td>
                <td style={{ ...td, textAlign: 'right', color: 'var(--text-tertiary)' }}>{i + 1}</td>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  {r.nameTh && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.nameTh}</div>}
                </td>
                <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{r.taxId || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>{r.count}</td>
                <td style={{ ...td, textAlign: 'right', color: '#15803D', fontWeight: 600 }}>{moneyTight(r.wht)}</td>
                <td style={{ ...td, color: r.exportedAt ? 'var(--text-secondary)' : 'var(--text-tertiary)', fontSize: 12 }}>
                  {r.exportedAt ? fmtDT(r.exportedAt) : '—'}
                  {/* Exported after the last Filed tick, or never ticked (report d5e686b4) — the NIL pill's colours. */}
                  {r.exportedAt > r.filedAt && <span title={t('fc_unfiled_tip')} style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: '#FFFBEB', color: '#B45309', whiteSpace: 'nowrap' }}>{t('fc_unfiled')}</span>}
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <label style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!r.filedAt} onChange={e => setFiled([r.clientId], e.target.checked)} style={{ cursor: 'pointer', width: 15, height: 15 }} />
                    {r.filedAt ? <span style={{ fontSize: 9.5, fontWeight: 700, color: '#15803D', whiteSpace: 'nowrap' }}>{fmtDT(r.filedAt)}</span> : null}
                  </label>
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <DlBtn onRun={() => dlOne(r)} title={t('fc_download')} style={{ padding: '4px 8px', border: 'none', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontSize: 16 }}><i className="ti ti-download" /></DlBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && rows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 14px', background: 'var(--bg-surface)', borderTop: '1px solid var(--border)', fontSize: 12.5 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('fc_selected')} <b style={{ color: 'var(--text-primary)' }}>{sel.size}</b></span>
            <span style={{ color: 'var(--text-secondary)' }}>{t('fc_wht')} <b style={{ color: 'var(--text-primary)' }}>{moneyTight(selWht)}</b></span>
            <div style={{ flex: 1 }} />
            <button onClick={() => setFiled(Array.from(sel), true)} disabled={!sel.size} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 700, cursor: sel.size ? 'pointer' : 'not-allowed', opacity: sel.size ? 1 : 0.5 }}><i className="ti ti-check" /> {t('fc_mark_filed')}</button>
            <button onClick={() => dlZip(Array.from(sel))} disabled={busy || !sel.size} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: (busy || !sel.size) ? 'not-allowed' : 'pointer', opacity: (busy || !sel.size) ? 0.55 : 1 }}><i className="ti ti-download" /> {t('fc_download_selected')}</button>
          </div>
        )}
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 10, lineHeight: 1.6 }}>{t('fc_hint')}</div>

      {/* After an export: "Submitted on the RD website?" → the companies + "Mark N as filed" (see askFiled above).
          No click-outside close: it is answered with a button, every time. */}
      {ask && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div role="dialog" aria-modal="true" style={{ background: 'var(--bg-panel)', borderRadius: 12, width: 'min(560px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 800 }}>
              {FORMS.find(([k]) => k === ask.form)?.[1]} · {MONTHS[ask.month - 1]} {ask.year}
            </div>
            {ask.step === 1 ? (
              <div style={{ padding: '16px 18px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{ask.rows.length === 1 ? t('fc_ask_q_one') : t('fc_ask_q', { n: ask.rows.length })}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{t('fc_ask_note')}</div>
              </div>
            ) : (
              <div style={{ padding: '12px 18px', overflowY: 'auto' }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 2 }}>{ask.rows.length === 1 ? t('fc_conf_lead_one') : t('fc_conf_lead', { n: ask.rows.length })}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 6 }}>{t('fc_conf_untick')}</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {ask.rows.map((r, i) => (
                      <tr key={r.clientId}>
                        <td style={{ ...td, width: 32, textAlign: 'center' }}>
                          <input type="checkbox" checked={ask.ids.includes(r.clientId)} disabled={askBusy} style={{ cursor: 'pointer' }}
                            onChange={() => setAsk({ ...ask, ids: ask.ids.includes(r.clientId) ? ask.ids.filter(x => x !== r.clientId) : ask.ids.concat(r.clientId) })} />
                        </td>
                        <td style={{ ...td, width: 32, textAlign: 'right', color: 'var(--text-tertiary)' }}>{i + 1}</td>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{r.name}</div>
                          {r.nameTh && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.nameTh}</div>}
                        </td>
                        <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{r.taxId || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {askErr && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 8, padding: '7px 11px', marginTop: 10 }}>{askErr}</div>}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
              {ask.step === 1 ? (
                <>
                  <button onClick={() => setAsk(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>{t('fc_ask_not_yet')}</button>
                  <button onClick={() => setAsk({ ...ask, step: 2 })} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    <i className="ti ti-check" /> {t('fc_ask_yes')}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => { setAskErr(''); setAsk({ ...ask, step: 1 }); }} disabled={askBusy} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: askBusy ? 'default' : 'pointer', fontSize: 13 }}>{t('fc_ask_back')}</button>
                  <button onClick={confirmFiled} disabled={askBusy || !ask.ids.length}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (askBusy || !ask.ids.length) ? 'not-allowed' : 'pointer', opacity: (askBusy || !ask.ids.length) ? 0.55 : 1 }}>
                    <i className={`ti ${askBusy ? 'ti-loader-2' : 'ti-check'}`} style={{ animation: askBusy ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} />
                    {t('fc_conf_mark', { n: ask.ids.length })}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
